import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Table, Card, Space, Tag, Input, Select, Button, Row, Col,
  Statistic, message, Tooltip, Modal, Descriptions, Switch, Rate, Form, InputNumber
} from 'antd';
import {
  AppstoreOutlined, SearchOutlined, DownloadOutlined, EyeOutlined,
  CheckCircleOutlined, CloseCircleOutlined, ClockCircleOutlined,
  ShopOutlined, StarOutlined, ExclamationCircleOutlined, EditOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import api from '../../services/api';
import { exportToExcel } from '../../utils/excelExport';
import BackButton from '../../components/BackButton';

const AdminServiceList = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const currentLang = i18n.language;

  const [loading, setLoading] = useState(false);
  const [services, setServices] = useState([]);
  const [summary, setSummary] = useState({});
  const [partners, setPartners] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedService, setSelectedService] = useState(null);
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [approvalModalVisible, setApprovalModalVisible] = useState(false);
  const [approvalAction, setApprovalAction] = useState(null);
  const [rejectionReason, setRejectionReason] = useState('');
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [editingService, setEditingService] = useState(null);
  const [editForm] = Form.useForm();

  const [filters, setFilters] = useState({
    approval_status: null,
    partner: null,
    category: null,
    is_active: null,
    search: '',
  });

  useEffect(() => {
    loadServices();
    loadPartners();
    loadCategories();
  }, []);

  const loadServices = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.approval_status) params.approval_status = filters.approval_status;
      if (filters.partner) params.partner = filters.partner;
      if (filters.category) params.category = filters.category;
      if (filters.is_active !== null) params.is_active = filters.is_active;
      if (filters.search) params.search = filters.search;

      const response = await api.get('/services/admin/services/', { params });
      setServices(response.data.services || []);
      setSummary(response.data.summary || {});
    } catch (error) {
      message.error(t('admin.services.loadFailed'));
    } finally {
      setLoading(false);
    }
  };

  const loadPartners = async () => {
    try {
      const response = await api.get('/partners/admin/');
      setPartners(response.data.results || response.data || []);
    } catch (error) {
      console.error('Failed to load partners');
    }
  };

  const loadCategories = async () => {
    try {
      const response = await api.get('/partners/service-categories/');
      setCategories(response.data.results || response.data || []);
    } catch (error) {
      console.error('Failed to load categories');
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const applyFilters = () => {
    loadServices();
  };

  const clearFilters = () => {
    setFilters({
      approval_status: null,
      partner: null,
      category: null,
      is_active: null,
      search: '',
    });
  };

  const showServiceDetail = async (serviceId) => {
    setDetailLoading(true);
    setDetailModalVisible(true);
    try {
      const response = await api.get(`/services/admin/services/${serviceId}/`);
      setSelectedService(response.data);
    } catch (error) {
      message.error(t('admin.services.loadDetailFailed'));
    } finally {
      setDetailLoading(false);
    }
  };

  const handleToggleActive = async (serviceId, currentValue) => {
    try {
      await api.patch(`/services/admin/services/${serviceId}/`, { is_active: !currentValue });
      message.success(!currentValue ? t('admin.services.serviceActivated') : t('admin.services.serviceDeactivated'));
      loadServices();
    } catch (error) {
      message.error(t('admin.services.toggleFailed'));
    }
  };

  const openApprovalModal = (service, action) => {
    setSelectedService(service);
    setApprovalAction(action);
    setRejectionReason('');
    setApprovalModalVisible(true);
  };

  const handleApproval = async () => {
    try {
      await api.post(`/services/admin/services/${selectedService.id}/approval/`, {
        action: approvalAction,
        reason: rejectionReason,
      });
      message.success(
        approvalAction === 'approve'
          ? t('admin.services.serviceApproved')
          : t('admin.services.serviceRejected')
      );
      setApprovalModalVisible(false);
      loadServices();
    } catch (error) {
      message.error(t('admin.services.approvalFailed'));
    }
  };

  const openEditModal = async (serviceId) => {
    try {
      const response = await api.get(`/services/admin/services/${serviceId}/`);
      const service = response.data;
      setEditingService(service);
      editForm.setFieldsValue({
        name_en: service.name_en,
        name_el: service.name_el,
        description_en: service.description_en,
        description_el: service.description_el,
        price: service.price,
        duration_minutes: service.duration_minutes,
        capacity: service.capacity,
        is_active: service.is_active,
      });
      setEditModalVisible(true);
    } catch (error) {
      message.error(t('admin.services.loadDetailFailed'));
    }
  };

  const handleEditSave = async () => {
    try {
      const values = await editForm.validateFields();
      await api.patch(`/services/admin/services/${editingService.id}/`, values);
      message.success(t('admin.services.updateSuccess'));
      setEditModalVisible(false);
      setEditingService(null);
      loadServices();
    } catch (error) {
      if (error.errorFields) {
        // Form validation error
        return;
      }
      message.error(t('admin.services.updateFailed'));
    }
  };

  const handleExport = () => {
    const exportData = services.map(service => ({
      ...service,
      created_at: dayjs(service.created_at).format('DD/MM/YYYY HH:mm'),
    }));
    const exportColumns = [
      { title: 'ID', dataIndex: 'id' },
      { title: t('admin.services.name'), dataIndex: currentLang === 'el' ? 'name_el' : 'name_en' },
      { title: t('admin.services.partner'), dataIndex: 'partner_name' },
      { title: t('admin.services.category'), dataIndex: 'category_name' },
      { title: t('admin.services.price'), dataIndex: 'price' },
      { title: t('admin.services.duration'), dataIndex: 'duration_minutes' },
      { title: t('admin.services.rating'), dataIndex: 'average_rating' },
      { title: t('admin.services.reviews'), dataIndex: 'review_count' },
      { title: t('admin.services.approvalStatus'), dataIndex: 'approval_status' },
      { title: t('admin.services.active'), dataIndex: 'is_active' },
      { title: t('admin.services.createdAt'), dataIndex: 'created_at' },
    ];
    exportToExcel(exportData, exportColumns, 'services', t('admin.services.title'));
    message.success(t('admin.services.exportSuccess'));
  };

  const getApprovalStatusColor = (status) => {
    const colors = {
      PENDING: 'orange',
      APPROVED: 'green',
      REJECTED: 'red'
    };
    return colors[status] || 'default';
  };

  const getApprovalStatusIcon = (status) => {
    const icons = {
      PENDING: <ClockCircleOutlined />,
      APPROVED: <CheckCircleOutlined />,
      REJECTED: <CloseCircleOutlined />
    };
    return icons[status] || <ClockCircleOutlined />;
  };

  // Auto-reload when filters change via stat cards
  const [autoReload, setAutoReload] = useState(false);
  useEffect(() => {
    if (autoReload) {
      loadServices();
      setAutoReload(false);
    }
  }, [filters, autoReload]);

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 60,
      fixed: 'left',
    },
    {
      title: t('admin.services.name'),
      key: 'name',
      width: 200,
      ellipsis: true,
      render: (_, record) => (
        <div style={{ overflow: 'hidden' }}>
          <div style={{ fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{currentLang === 'el' ? record.name_el : record.name_en}</div>
          {record.average_rating && (
            <div style={{ fontSize: 12 }}>
              <StarOutlined style={{ color: '#faad14' }} /> {record.average_rating.toFixed(1)} ({record.review_count})
            </div>
          )}
        </div>
      ),
    },
    {
      title: t('admin.services.partner'),
      key: 'partner',
      width: 150,
      ellipsis: true,
      render: (_, record) => (
        <div style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          <ShopOutlined /> {record.partner_name}
        </div>
      ),
    },
    {
      title: t('admin.services.category'),
      dataIndex: 'category_name',
      key: 'category_name',
      width: 130,
      ellipsis: true,
    },
    {
      title: t('admin.services.price'),
      key: 'price',
      width: 90,
      render: (_, record) => (
        <span>{record.price}</span>
      ),
    },
    {
      title: t('admin.services.approvalStatus'),
      dataIndex: 'approval_status',
      key: 'approval_status',
      width: 120,
      render: (status) => (
        <Tag color={getApprovalStatusColor(status)} icon={getApprovalStatusIcon(status)}>
          {t(`admin.services.statuses.${status.toLowerCase()}`)}
        </Tag>
      ),
    },
    {
      title: t('admin.services.active'),
      key: 'is_active',
      width: 70,
      render: (_, record) => (
        <Switch
          checked={record.is_active}
          onChange={() => handleToggleActive(record.id, record.is_active)}
          size="small"
        />
      ),
    },
    {
      title: t('common.actions'),
      key: 'actions',
      width: 140,
      fixed: 'right',
      render: (_, record) => (
        <Space size="small">
          <Tooltip title={t('common.view')}>
            <Button
              type="link"
              size="small"
              icon={<EyeOutlined />}
              onClick={() => showServiceDetail(record.id)}
            />
          </Tooltip>
          <Tooltip title={t('common.edit')}>
            <Button
              type="link"
              size="small"
              icon={<EditOutlined />}
              onClick={() => openEditModal(record.id)}
            />
          </Tooltip>
          {record.approval_status === 'PENDING' && (
            <>
              <Tooltip title={t('admin.services.approve')}>
                <Button
                  type="link"
                  size="small"
                  icon={<CheckCircleOutlined />}
                  style={{ color: '#52c41a' }}
                  onClick={() => openApprovalModal(record, 'approve')}
                />
              </Tooltip>
              <Tooltip title={t('admin.services.reject')}>
                <Button
                  type="link"
                  size="small"
                  icon={<CloseCircleOutlined />}
                  style={{ color: '#ff4d4f' }}
                  onClick={() => openApprovalModal(record, 'reject')}
                />
              </Tooltip>
            </>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: '24px' }}>
      <BackButton to="/admin/dashboard" label={t('admin.backToDashboard')} />

      {/* Summary Stats - Clickable */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} md={6}>
          <Tooltip title={t('admin.services.clickToShowAll')}>
            <Card
              hoverable
              onClick={() => {
                setFilters({ approval_status: null, partner: null, category: null, is_active: null, search: '' });
                setAutoReload(true);
              }}
              style={{ cursor: 'pointer' }}
            >
              <Statistic
                title={t('admin.services.totalServices')}
                value={summary.total || 0}
                prefix={<AppstoreOutlined />}
              />
            </Card>
          </Tooltip>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Tooltip title={t('admin.services.clickToShowPending')}>
            <Card
              hoverable
              onClick={() => {
                setFilters({ approval_status: 'PENDING', partner: null, category: null, is_active: null, search: '' });
                setAutoReload(true);
              }}
              style={{
                cursor: 'pointer',
                backgroundColor: summary.pending_approval > 0 ? '#fff7e6' : undefined,
                border: filters.approval_status === 'PENDING' ? '2px solid #faad14' : undefined
              }}
            >
              <Statistic
                title={t('admin.services.pendingApproval')}
                value={summary.pending_approval || 0}
                prefix={<ExclamationCircleOutlined />}
                valueStyle={{ color: summary.pending_approval > 0 ? '#faad14' : undefined }}
              />
            </Card>
          </Tooltip>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Tooltip title={t('admin.services.clickToShowApproved')}>
            <Card
              hoverable
              onClick={() => {
                setFilters({ approval_status: 'APPROVED', partner: null, category: null, is_active: null, search: '' });
                setAutoReload(true);
              }}
              style={{ cursor: 'pointer', border: filters.approval_status === 'APPROVED' ? '2px solid #52c41a' : undefined }}
            >
              <Statistic
                title={t('admin.services.approvedServices')}
                value={summary.approved || 0}
                prefix={<CheckCircleOutlined />}
                valueStyle={{ color: '#52c41a' }}
              />
            </Card>
          </Tooltip>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title={t('admin.services.avgRating')}
              value={summary.avg_rating ? summary.avg_rating.toFixed(1) : '-'}
              prefix={<StarOutlined />}
              valueStyle={{ color: '#faad14' }}
            />
          </Card>
        </Col>
      </Row>

      <Card
        title={
          <span style={{ fontSize: '20px', fontWeight: 'bold' }}>
            <AppstoreOutlined style={{ marginRight: 8 }} />
            {t('admin.services.title')}
          </span>
        }
      >
        {/* Filters */}
        <Row gutter={16} style={{ marginBottom: 16 }}>
          <Col xs={24} sm={12} md={4}>
            <Input
              placeholder={t('admin.services.searchPlaceholder')}
              prefix={<SearchOutlined />}
              value={filters.search}
              onChange={(e) => handleFilterChange('search', e.target.value)}
              onPressEnter={applyFilters}
            />
          </Col>
          <Col xs={24} sm={12} md={3}>
            <Select
              placeholder={t('admin.services.filterByApproval')}
              style={{ width: '100%' }}
              allowClear
              value={filters.approval_status}
              onChange={(value) => handleFilterChange('approval_status', value)}
            >
              <Select.Option value="PENDING">{t('admin.services.statuses.pending')}</Select.Option>
              <Select.Option value="APPROVED">{t('admin.services.statuses.approved')}</Select.Option>
              <Select.Option value="REJECTED">{t('admin.services.statuses.rejected')}</Select.Option>
            </Select>
          </Col>
          <Col xs={24} sm={12} md={4}>
            <Select
              placeholder={t('admin.services.filterByPartner')}
              style={{ width: '100%' }}
              allowClear
              showSearch
              optionFilterProp="children"
              value={filters.partner}
              onChange={(value) => handleFilterChange('partner', value)}
            >
              {partners.map(p => (
                <Select.Option key={p.id} value={p.id}>
                  {p.business_name || p.store_name}
                </Select.Option>
              ))}
            </Select>
          </Col>
          <Col xs={24} sm={12} md={4}>
            <Select
              placeholder={t('admin.services.filterByCategory')}
              style={{ width: '100%' }}
              allowClear
              value={filters.category}
              onChange={(value) => handleFilterChange('category', value)}
            >
              {categories.map(c => (
                <Select.Option key={c.id} value={c.id}>
                  {currentLang === 'el' ? c.name_el : c.name_en}
                </Select.Option>
              ))}
            </Select>
          </Col>
          <Col xs={24} sm={12} md={9}>
            <Space>
              <Button type="primary" onClick={applyFilters}>
                {t('admin.applyFilters')}
              </Button>
              <Button onClick={clearFilters}>
                {t('admin.clearFilters')}
              </Button>
              <Button
                icon={<DownloadOutlined />}
                onClick={handleExport}
                disabled={!services.length}
              >
                {t('admin.exportExcel')}
              </Button>
            </Space>
          </Col>
        </Row>

        {/* Table */}
        <Table
          columns={columns}
          dataSource={services}
          rowKey="id"
          loading={loading}
          pagination={{ pageSize: 20 }}
          scroll={{ x: 1000 }}
        />
      </Card>

      {/* Detail Modal */}
      <Modal
        title={`${t('admin.services.serviceDetails')} #${selectedService?.id}`}
        open={detailModalVisible}
        onCancel={() => {
          setDetailModalVisible(false);
          setSelectedService(null);
        }}
        footer={[
          <Button key="close" onClick={() => setDetailModalVisible(false)}>
            {t('common.close')}
          </Button>
        ]}
        width={700}
        loading={detailLoading}
      >
        {selectedService && (
          <div>
            <Descriptions bordered size="small" column={2}>
              <Descriptions.Item label={t('admin.services.name')} span={2}>
                {currentLang === 'el' ? selectedService.name_el : selectedService.name_en}
              </Descriptions.Item>
              <Descriptions.Item label={t('admin.services.partner')}>
                {selectedService.partner?.name}
              </Descriptions.Item>
              <Descriptions.Item label={t('admin.services.category')}>
                {selectedService.category?.name}
              </Descriptions.Item>
              <Descriptions.Item label={t('admin.services.price')}>
                {selectedService.price}
              </Descriptions.Item>
              <Descriptions.Item label={t('admin.services.duration')}>
                {selectedService.duration_minutes} {t('admin.services.minutes')}
              </Descriptions.Item>
              <Descriptions.Item label={t('admin.services.capacity')}>
                {selectedService.capacity}
              </Descriptions.Item>
              <Descriptions.Item label={t('admin.services.rating')}>
                {selectedService.average_rating ? (
                  <Rate disabled defaultValue={selectedService.average_rating} allowHalf />
                ) : '-'}
                {selectedService.review_count > 0 && ` (${selectedService.review_count})`}
              </Descriptions.Item>
              <Descriptions.Item label={t('admin.services.approvalStatus')}>
                <Tag color={getApprovalStatusColor(selectedService.approval_status)}>
                  {selectedService.approval_status}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label={t('admin.services.active')}>
                <Tag color={selectedService.is_active ? 'green' : 'red'}>
                  {selectedService.is_active ? t('common.active') : t('common.inactive')}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label={t('admin.services.createdAt')}>
                {dayjs(selectedService.created_at).format('DD/MM/YYYY HH:mm')}
              </Descriptions.Item>
              {selectedService.approved_at && (
                <Descriptions.Item label={t('admin.services.approvedAt')}>
                  {dayjs(selectedService.approved_at).format('DD/MM/YYYY HH:mm')}
                </Descriptions.Item>
              )}
              {selectedService.rejection_reason && (
                <Descriptions.Item label={t('admin.services.rejectionReason')} span={2}>
                  {selectedService.rejection_reason}
                </Descriptions.Item>
              )}
            </Descriptions>

            {selectedService.description_en && (
              <div style={{ marginTop: 16 }}>
                <h4>{t('admin.services.description')}</h4>
                <p>{currentLang === 'el' ? selectedService.description_el : selectedService.description_en}</p>
              </div>
            )}

            {selectedService.animal_categories?.length > 0 && (
              <div style={{ marginTop: 16 }}>
                <h4>{t('admin.services.animalCategories')}</h4>
                <Space>
                  {selectedService.animal_categories.map(ac => (
                    <Tag key={ac.id}>{ac.name}</Tag>
                  ))}
                </Space>
              </div>
            )}
          </div>
        )}
      </Modal>

      {/* Approval Modal */}
      <Modal
        title={approvalAction === 'approve' ? t('admin.services.approveService') : t('admin.services.rejectService')}
        open={approvalModalVisible}
        onCancel={() => setApprovalModalVisible(false)}
        onOk={handleApproval}
        okText={approvalAction === 'approve' ? t('admin.services.approve') : t('admin.services.reject')}
        okButtonProps={{
          danger: approvalAction === 'reject',
        }}
      >
        {approvalAction === 'approve' ? (
          <p>{t('admin.services.confirmApprove', { name: selectedService?.name_en })}</p>
        ) : (
          <div>
            <p>{t('admin.services.confirmReject', { name: selectedService?.name_en })}</p>
            <Input.TextArea
              rows={3}
              placeholder={t('admin.services.rejectionReasonPlaceholder')}
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value)}
            />
          </div>
        )}
      </Modal>

      {/* Edit Modal */}
      <Modal
        title={t('admin.services.editService')}
        open={editModalVisible}
        onCancel={() => {
          setEditModalVisible(false);
          setEditingService(null);
          editForm.resetFields();
        }}
        onOk={handleEditSave}
        okText={t('common.save')}
        cancelText={t('common.cancel')}
        width={700}
      >
        <Form
          form={editForm}
          layout="vertical"
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="name_en"
                label={t('admin.services.nameEn')}
                rules={[{ required: true, message: t('admin.services.nameRequired') }]}
              >
                <Input />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="name_el"
                label={t('admin.services.nameEl')}
                rules={[{ required: true, message: t('admin.services.nameRequired') }]}
              >
                <Input />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="description_en"
                label={t('admin.services.descriptionEn')}
              >
                <Input.TextArea rows={4} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="description_el"
                label={t('admin.services.descriptionEl')}
              >
                <Input.TextArea rows={4} />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                name="price"
                label={t('admin.services.price')}
                rules={[{ required: true, message: t('admin.services.priceRequired') }]}
              >
                <InputNumber
                  min={0}
                  step={0.01}
                  style={{ width: '100%' }}
                  addonAfter="€"
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="duration_minutes"
                label={t('admin.services.duration')}
                rules={[{ required: true, message: t('admin.services.durationRequired') }]}
              >
                <InputNumber
                  min={1}
                  style={{ width: '100%' }}
                  addonAfter={t('admin.services.minutes')}
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="capacity"
                label={t('admin.services.capacity')}
              >
                <InputNumber
                  min={1}
                  style={{ width: '100%' }}
                />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item
            name="is_active"
            label={t('admin.services.active')}
            valuePropName="checked"
          >
            <Switch />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default AdminServiceList;
